'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'
import { Container } from '@mui/material'
import { usePathname, useRouter } from 'next/navigation'
import { useFlags } from 'launchdarkly-react-client-sdk'
import LoadingComponent from '@/app/components/ui/loadingcomponent'
import ErrorCardView, { ErrorCardVariant } from '@/app/components/ui/errorComponents/errorCardView'
import { AppRoutes } from '../constants/routes'
import { COMPASS_POC_EMAILS, getSubFromIdToken, isCompassPocUser } from '../constants/compassPoc'
import { formatCompanyErrorSupport } from '../../app/lib/errors/apiError'
import { useCompanyContext } from './companyContext'
import { usePreferences } from './preferencesContext'
import { useUserContext } from './userContext'
import { trackLoginEvent } from '../../app/lib/analytics/analyticsEventTags'
import { pushToDataLayer } from '../../app/lib/analytics/analyticsTracking'
import { PermissionGuard } from '../../app/lib/auth/permissionGuard'
import LoginView from '@/app/components/LoginView'
import inter, { localInter } from '@styles/fonts'

type RenderState = 'loading' | 'unauthenticated' | 'unauthorized' | 'ready'

export const RouteGuardProvider: React.FC<{
	children: React.ReactNode
}> = ({ children }) => {
	const { status, data: session } = useSession()
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const sessionEmail = (session?.user?.email ?? getSubFromIdToken((session as any)?.idToken) ?? '').toLowerCase()
	const router = useRouter()
	const pathName = usePathname()

	const { tempBoss2102EnableMultiCompanyPage, tempBoss2676EnableCompassPoc } = useFlags()

	const { isLoading: userLoading, signin, signout } = useUserContext()
	const {
		selectedCompanyId,
		availableCompaniesById,
		isLoading: companyLoading,
		companyContextError,
		hasAcceptedLatestTerms,
		isMultiCompany,
		permissions,
	} = useCompanyContext()
	const { isLoading: preferencesLoading } = usePreferences()

	const [renderState, setRenderState] = useState<RenderState>('loading')

	const supportLines = useMemo(() => formatCompanyErrorSupport(companyContextError), [companyContextError])

	// ── Single route-guard decision tree ─────────────────────────────────
	// Every code path is visible in one place so the ordering is obvious.
	// The tree evaluates top-to-bottom; the first branch that matches wins.
	useEffect(() => {
		// Default to showing the loader. Only the code paths that resolve to
		// "show the page" set it to false. This prevents flicker when
		// redirecting — the loader stays up continuously.
		setRenderState('loading')

		// ── 1. Wait for all parent contexts to finish loading ─────────────
		if (userLoading || companyLoading || preferencesLoading) {
			return
		}

		// ── 2. Public routes — allow access without permission checks ───
		// Authenticated users on HOME fall through to step 4 which handles
		// company selection, permissions, and redirect logic.
		if (PermissionGuard.isPublicRoute(pathName) && !(pathName === AppRoutes.TERMS_AND_CONDITIONS && status === 'unauthenticated') && !(pathName === AppRoutes.HOME && status === 'authenticated')) {
			setRenderState('ready')
			return
		}

		// Authenticated on home page — handled by step 4 alongside all other
		// authenticated routing decisions (company selection, permissions, terms).

		// ── 3. Unauthenticated on a guarded path ─────────────────────────
		if (status === 'unauthenticated') {
			console.debug(`RouteGuard – unauthenticated on ${pathName}, triggering signin`)
			// signin()
			setRenderState('unauthenticated')
			return
		}

		// ── 4. Authenticated — company → permissions → terms → route ─────
		// At this point status is guaranteed to be 'authenticated' because:
		//   - 'loading' is caught by step 1
		//   - 'unauthenticated' is caught by step 3

		// 4a. Compass POC — requires only authentication, no company/permissions
		if (pathName === AppRoutes.COMPASS_POC_DASHBOARD) {
			if (tempBoss2676EnableCompassPoc && isCompassPocUser(sessionEmail)) {
				setRenderState('ready')
				return
			}
		}

		// 4b. Company selection page handling.
		if (pathName === AppRoutes.COMPANY_SELECTION) {
			if (!selectedCompanyId) {
				// No company selected — stay on company selection page
				setRenderState('ready')
				return
			}
			// Company was just selected — skip remaining checks and redirect
			// to landing page. This prevents the company selection page from
			// flashing while the route guard re-evaluates.
			if (permissions?.length) {
				router.push(PermissionGuard.getRedirectRoute(permissions))
				return
			}
		}

		// 4c. Has companies but none selected — redirect or wait for auto-select
		if (!selectedCompanyId) {
			if (!availableCompaniesById?.length) {
				if (tempBoss2676EnableCompassPoc && COMPASS_POC_EMAILS.has(sessionEmail)) {
					router.push(AppRoutes.COMPASS_POC_DASHBOARD)
					return
				}
				console.warn('[UNAUTHORIZED] User has no companies available', {
					selectedCompanyId,
					availableCompaniesCount: availableCompaniesById?.length,
					permissionsLoaded: permissions !== null,
					permissionsCount: permissions?.length ?? 'null',
				})
				pushToDataLayer(trackLoginEvent(false))
				setRenderState('unauthorized')
				return
			}
			if (tempBoss2102EnableMultiCompanyPage && isMultiCompany) {
				router.push(AppRoutes.COMPANY_SELECTION)
			} else if (!tempBoss2102EnableMultiCompanyPage) {
				console.error('[RouteGuard] No company selected and multi-company disabled, redirecting to unauthorized')
				pushToDataLayer(trackLoginEvent(false))
				setRenderState('unauthorized')
			}
			return
		}

		// 4d. No permissions
		if (!permissions?.length) {
			console.error('RouteGuard [UNAUTHORIZED] User has no permissions - access denied', {
				selectedCompanyId,
				availableCompanies: availableCompaniesById?.length,
			})
			pushToDataLayer(trackLoginEvent(false))
			setRenderState('unauthorized')
			return
		}

		// 4f. Authenticated on home page with permissions — redirect to landing
		if (pathName === AppRoutes.HOME) {
			pushToDataLayer(trackLoginEvent(true))
			router.push(PermissionGuard.getRedirectRoute(permissions))
			return
		}

		// 4g. Terms not accepted
		if (!hasAcceptedLatestTerms) {
			router.push(AppRoutes.TERMS_AND_CONDITIONS)
			return
		}

		// 4g. Permission-guarded route — verify route-level access
		if (PermissionGuard.isPermissionGuarded(pathName) && !PermissionGuard.hasRouteAccess(permissions, pathName)) {
			console.warn(`RouteGuard – missing permissions for ${pathName}, redirecting`)
			router.push(PermissionGuard.getRedirectRoute(permissions))
			return
		}

		// ── 5. All checks passed — show the page ─────────────────────────
		setRenderState('ready')
	}, [
		status,
		sessionEmail,
		pathName,
		router,
		userLoading,
		companyLoading,
		preferencesLoading,
		selectedCompanyId,
		availableCompaniesById,
		permissions,
		hasAcceptedLatestTerms,
		tempBoss2102EnableMultiCompanyPage,
		tempBoss2676EnableCompassPoc,
		isMultiCompany,
		companyContextError,
		signin,
	])

	if (renderState === 'unauthenticated') {
		return (
			<div className={`${inter.className} ${localInter.className}`}>
				<LoginView/>
			</div>
		)
	}

	if (renderState === 'unauthorized') {
		return (
			<ErrorCardView
				variant={ErrorCardVariant.UNAUTHORIZED}
				onPrimaryAction={signout}
				supportLines={supportLines}
				supportTextTestId="unauthorized-error-support-text"
			/>
		)
	}

	if (renderState === 'loading') {
		return (
			<Container
				data-testid="loading-container"
				maxWidth={false}
				disableGutters
				sx={{
					height: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<LoadingComponent />
			</Container>
		)
	}

	return <>{children}</>
}
